import { EmbeddingOrigin } from './resolveEmbeddingOrigin';
import { isEmbeddingHostAllowed } from './isEmbeddingHostAllowed';

export type HostAccessDecision =
  | { outcome: 'allowed' }
  | { outcome: 'unverified' }
  | { outcome: 'denied'; untrusted: string[] };

/**
 * Decides whether the page embedding us is one the partner's config vouches
 * for.
 *
 * This is a misconfiguration guard, not a security boundary — every signal it
 * reads can be withheld or forged by a determined embedder. That is why it
 * leans towards letting visitors through: the cost of blocking a real customer
 * over a missing browser signal is higher than the cost of rendering inside an
 * unrecognized frame.
 */
export function decideHostAccess({
  env,
  whitelist,
  embedding,
}: {
  env: string;
  whitelist?: string[];
  embedding: EmbeddingOrigin;
}): HostAccessDecision {
  // Only production carries a real whitelist: the lower environments default to
  // '*' and are where both our teams and the partners' do their testing.
  if (env !== 'production') {
    return { outcome: 'allowed' };
  }

  // Nothing configured is nothing to enforce.
  if (!whitelist || whitelist.length === 0) {
    return { outcome: 'allowed' };
  }

  if (embedding.state === 'not-embedded') {
    return { outcome: 'allowed' };
  }

  // Reported, but not blocked — see the note above about failing open.
  if (embedding.state === 'undeterminable') {
    return { outcome: 'unverified' };
  }

  // Every frame between the visitor and us has to be accounted for: a
  // whitelisted page at the top of the chain does not excuse an unrecognized
  // frame in the middle. Only the offenders are returned, so the report can
  // point at what needs fixing rather than at the whole chain.
  const untrusted = embedding.ancestors.filter(
    (ancestor) => !isEmbeddingHostAllowed(ancestor, whitelist)
  );

  return untrusted.length > 0
    ? { outcome: 'denied', untrusted }
    : { outcome: 'allowed' };
}
